import React, { Suspense } from 'react';
import {
  Route,
  RouteComponentProps,
  Switch,
  withRouter
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { ToastContainer, cssTransition } from 'react-toastify';
import classNames from 'classnames';

import ROUTES, {
  AUTH_ROUTES,
  getRoutes,
  IRouteOptions
} from '@app/src/common/constants/routeUrls';
import routerStore, { ILocation } from '@app/src/common/store/RouterStore';

import ErrorBoundary from '@app/src/common/components/error/ErrorBoundary';
import ModalList from '@app/src/common/components/modals/ModalList';
import NotFound from '@app/src/common/components/not-found/NotFound';
import AppHeader from '@app/src/common/components/header/AppHeader';
import Sidebar from '@app/src/common/components/sidebar/Sidebar';
import { APP_CLASSNAME } from '@app/src/common/constants/common';

export interface IAppProps extends RouteComponentProps<any, any, any> {}

const bounce = cssTransition({
  enter: 'bounceIn',
  exit: 'bounceOut'
});

@observer
class App extends React.Component<IAppProps> {
  defineRoutes = () => {
    const { location, match, history } = this.props;
    getRoutes()
      .then(routes => {
        routerStore.updateRoutes(routes);
        routerStore.selectRoute(location, match, history);
      })
      .catch(() => null);
  };

  componentDidMount() {
    this.defineRoutes();
    this.updateViewPort();
  }

  componentDidUpdate() {
    this.updateRoutes();
  }

  get className() {
    const { location } = this.props;
    const classes = ['app'];
    if (!this.isWithoutBackground(location)) {
      classes.push('app-auth-page');
    }
    if (location.pathname === ROUTES.consentPage.path) {
      classes.push('app-no-layout');
    }
    return classes.join(' ');
  }

  private updateRoutes(): void {
    const { location, match, history } = this.props;
    const { pathname } = location;
    if (routerStore.isAuthorizedRoute(location)) {
      routerStore.targetPath = pathname;
    }
    routerStore.selectRoute(location, match, history);
  }

  isWithoutBackground = (location: any) => {
    const notAuthPage = location.pathname !== ROUTES.authPage.path;
    const notForgotPasswordPage = !location.pathname.startsWith(
      ROUTES.forgotPasswordPage.path
    );
    const notResetPasswordPage = !location.pathname.startsWith(
      ROUTES.resetPasswordPage.path
    );

    return notAuthPage && notForgotPasswordPage && notResetPasswordPage;
  };

  checkPathname = (location: ILocation) => {
    return routerStore.isAuthorizedRoute(location);
  };

  updateViewPort = () => {
    const responsiveSwitchBreakpoint = 768;
    const viewportNode = document.querySelector('meta[name="viewport"]');
    const documentNode = document.documentElement;
    if (viewportNode && documentNode) {
      if (documentNode.clientWidth < responsiveSwitchBreakpoint) {
        const content =
          'width=' +
          responsiveSwitchBreakpoint +
          ', initial-scale=' +
          documentNode.clientWidth / responsiveSwitchBreakpoint;
        viewportNode.setAttribute('content', content);
      }
    }
  };

  render() {
    const { location } = this.props;
    return (
      <>
        <ToastContainer icon={false} transition={bounce} />

        <div
          className={classNames(APP_CLASSNAME, {
            ['app-auth-page']: !this.isWithoutBackground(location),
            ['app-no-layout']: location.pathname === ROUTES.consentPage.path
          })}
        >
          <Route
            render={({ location: routeLocation }) =>
              this.checkPathname(routeLocation as any) ? (
                <AppHeader>
                  <ErrorBoundary>
                    <Suspense fallback={null}>
                      {Object.values(AUTH_ROUTES).map(
                        (route: IRouteOptions) => (
                          <Route
                            key={route.name}
                            path={route.path}
                            component={route.header}
                          />
                        )
                      )}
                    </Suspense>
                  </ErrorBoundary>
                </AppHeader>
              ) : null
            }
          />
          <Route
            render={({ location: routeLocation }) =>
              this.checkPathname(routeLocation as any) ? <Sidebar /> : null
            }
          />
          <main className="app-content">
            <Suspense fallback={null}>
              <ErrorBoundary>
                <Switch>
                  <Route
                    exact
                    path={ROUTES.homePage.path}
                    component={ROUTES.homePage.component}
                  />
                  <Route
                    exact
                    path={ROUTES.authPage.path}
                    component={ROUTES.authPage.component}
                  />
                  <Route
                    exact
                    path={ROUTES.logOutPage.path}
                    component={ROUTES.logOutPage.component}
                  />
                  <Route
                    exact
                    path={ROUTES.notAuthorizedPage.path}
                    component={ROUTES.notAuthorizedPage.component}
                  />
                  <Route
                    exact
                    path={ROUTES.serverErrorPage.path}
                    component={ROUTES.serverErrorPage.component}
                  />
                  <Route
                    path={`${ROUTES.resetPasswordPage.path}/:key`}
                    component={ROUTES.resetPasswordPage.component}
                  />
                  <Route
                    path={ROUTES.forgotPasswordPage.path}
                    component={ROUTES.forgotPasswordPage.component}
                  />
                  <Route
                    path={ROUTES.consentPage.path}
                    component={ROUTES.consentPage.component}
                  />
                  {Object.values(AUTH_ROUTES).map((route: IRouteOptions) => (
                    <Route
                      key={route.path}
                      path={route.path}
                      component={route.component}
                    />
                  ))}
                  <Route component={NotFound} />
                </Switch>
              </ErrorBoundary>
              <ModalList />
            </Suspense>
          </main>
        </div>
      </>
    );
  }
}

export default withRouter(App);
